import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { useGlossaryBlueprintsDataContext } from '@/providers/GlossaryBlueprintsDataProvider';
import { useToast } from '@/hooks/use-toast';
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
import { useConfirmation } from '@/hooks/use-confirmation';
import { TagManager } from '@/components/ui';
import { GlossaryIdentifier } from '@/components/wizards/glossary/ObjectIdentifier';
import { CodeIdInput } from '@/components/wizards/glossary/CodeIdInput';
import { Label } from '@/components/ui/label';
import { TextAreaElement } from '@/components/wizards/wizard-elements/TextAreaElement';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/DefaultSelectDropdown';
import { Button } from '@/components/ui/button';
import { Save, Loader2, ExternalLink, RefreshCw } from 'lucide-react';
import { objectsStore, duplicateTranslationKeys } from '@/lib/stores/objectsStore';
import { apiService } from '@/lib/api-functions/api-service';
import { BaseBasicInfoProps } from './types';
import { OwnerAndPermissions } from './OwnerAndPermissions';

export function BasicInfoObject(props: BaseBasicInfoProps) {
  const { t } = usePlatformTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { confirmation } = useConfirmation();
  const { selectedGame } = useSimplifiedUserData();
  const { glossaryBlueprints } = useGlossaryBlueprintsDataContext();
  const [isRegeneratingKeys, setIsRegeneratingKeys] = useState(false);

  const {
    itemData,
    editedData,
    setEditedData,
    isEditing,
    setIsEditing,
    isSaving,
    availableStatuses,
    permissions,
    currentItem,
    onSave,
    onCancel,
  } = props;

  // Get blueprint tags for tag suggestions
  const blueprintTags = useMemo(() => {
    if (!currentItem?.blueprint_id) return [];
    const blueprint = glossaryBlueprints.find(bp => bp.id === currentItem.blueprint_id);
    return blueprint?.tags || [];
  }, [currentItem, glossaryBlueprints]);

  // Get blueprint info for the template link
  const blueprintInfo = useMemo(() => {
    if (!currentItem?.blueprint_id) return null;
    const blueprint = glossaryBlueprints.find(bp => bp.id === currentItem.blueprint_id);
    if (!blueprint) return null;

    return {
      id: blueprint.id,
      name: blueprint.name,
      version: blueprint.version,
    };
  }, [currentItem, glossaryBlueprints]);

  const handleBlueprintClick = () => {
    if (blueprintInfo) {
      navigate(`/templates/${blueprintInfo.id}`);
    }
  };

  // Handle regenerate translation keys
  const handleRegenerateTranslationKeys = () => {
    if (!currentItem || !selectedGame?.id) return;

    const confirmDialog = confirmation({
      title: 'Create New TMS Keys',
      description: 'This will create new translation keys for all translatable fields in this object. The content will be copied from the original keys. Are you sure you want to continue?',
      confirmText: 'Create New Keys',
      cancelText: 'Cancel',
      variant: 'default',
      onConfirm: async () => {
        setIsRegeneratingKeys(true);
        try {
          const objectItem = currentItem as any;

          // Duplicate translation keys for the object data
          const updatedData = await duplicateTranslationKeys(
            objectItem.data,
            selectedGame.id,
            toast
          );

          // Update the object with the new data containing new string_ids
          const result = await apiService.updateObject({
            id: objectItem.id,
            game_id: selectedGame.id,
            name: objectItem.name,
            type: objectItem.type || 'glossary',
            data: updatedData,
            tags: objectItem.tags,
            blueprint_id: objectItem.blueprint_id,
            identifiers: objectItem.identifiers,
            version: objectItem.version,
            folder: objectItem.folder,
            owner: objectItem.owner,
            edit_permissions: objectItem.edit_permissions,
            description: objectItem.description,
            status: objectItem.status,
          });

          if (!result.success) {
            throw new Error(result.error || 'Failed to regenerate translation keys');
          }

          // Update local store
          const currentData = objectsStore.getData();
          const updatedStoreData = currentData.map(item =>
            item.id === objectItem.id ? result.data : item
          );
          objectsStore.setData(updatedStoreData);

          toast({
            title: 'Success',
            description: 'Translation keys have been regenerated successfully. All fields now have new independent translation keys.',
          });

          // Refresh the page to show updated data
          window.location.reload();

          confirmDialog.dismiss();
        } catch (error) {
          console.error('Failed to regenerate translation keys:', error);
          toast({
            title: 'Error',
            description: error instanceof Error ? error.message : 'Failed to regenerate translation keys',
            variant: 'destructive',
          });
        } finally {
          setIsRegeneratingKeys(false);
        }
      },
      onCancel: () => {
        confirmDialog.dismiss();
      }
    });
  };

  return (
    <div className="flex flex-col h-full bg-background">
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Basic Information
            </h3>
          </div>

          {/* Template Link */}
          {blueprintInfo && (
            <div>
              <Label className="text-sm text-muted-foreground">Template</Label>
              <div
                onClick={handleBlueprintClick}
                className="mt-1 flex items-center gap-2 px-3 py-2 rounded border border-primary/30 cursor-pointer hover:bg-primary/10 transition-colors group w-full"
                title={`Open template: ${blueprintInfo.name}`}
              >
                <div className="flex items-start justify-start gap-2 w-full">
                  <span className="text-sm font-medium text-muted-foreground group-hover:text-foreground w-full">
                    {blueprintInfo.name}
                  </span>
                  <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-foreground" />
                </div>
              </div>
            </div>
          )}

          {/* Description */}
          <div>
            <Label htmlFor="basic-info-description" className="text-sm">
              {t('platform.common.description')}
            </Label>
            <TextAreaElement
              value={editedData?.description || ''}
              onChange={(value) => {
                if (!isEditing) setIsEditing(true);
                setEditedData({ ...editedData, description: value });
              }}
              placeholder={t('platform.common.enter_object_description')}
              disabled={!permissions.canEdit}
              rows={3}
              className="mt-1"
            />
          </div>



          {/* Identifiers */}
          <div>
            <GlossaryIdentifier
              identifiers={editedData?.identifiers || []}
              onIdentifiersChange={(identifiers) => {
                if (!isEditing) setIsEditing(true);
                setEditedData({ ...editedData, identifiers });
              }}
              mode={permissions.canEdit ? "edit" : "view"}
            />
          </div>

          {/* Code ID */}
          {selectedGame?.id && (
            <div>
              <CodeIdInput
                value={editedData?.code_id || ''}
                objectName={(currentItem as any).name || ''}
                folderId={(currentItem as any).folder}
                gameId={selectedGame.id}
                onChange={(codeId) => {
                  if (!isEditing) setIsEditing(true);
                  setEditedData({ ...editedData, code_id: codeId });
                }}
                mode={permissions.canEdit ? "edit" : "view"}
                existingObjectId={currentItem.id}
              />
            </div>
          )}

          {/* Status */}
          {Object.keys(availableStatuses).length > 0 && (
            <div className="relative z-[100]">
              <Label htmlFor="basic-info-status" className="text-sm">Status</Label>
              <Select
                value={editedData?.status || Object.keys(availableStatuses)[0]}
                onValueChange={(value) => {
                  if (!isEditing) setIsEditing(true);
                  setEditedData({ ...editedData, status: value });
                }}
                disabled={!permissions.canEdit}
              >
                <SelectTrigger className="mt-1 border border-grey-G5 rounded p-2 text-sm font-medium text-grey-G2">
                  <SelectValue placeholder="Select status">
                    {editedData?.status && availableStatuses[editedData.status] && (
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full border border-border flex-shrink-0" 
                          style={{ backgroundColor: availableStatuses[editedData.status].color }}
                        />
                        <span className="capitalize">{editedData.status}</span>
                      </div>
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="z-[100]" position="popper" side="top" sideOffset={4}>
                  {Object.entries(availableStatuses).map(([statusKey, statusData]) => (
                    <SelectItem key={statusKey} value={statusKey}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full border border-border flex-shrink-0"
                          style={{ backgroundColor: statusData.color }}
                        />
                        <span className="capitalize">{statusKey}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Owner and Edit Permissions */}
          <OwnerAndPermissions
            owner={editedData?.owner || null}
            editPermissions={editedData?.edit_permissions || 'Open'}
            onOwnerChange={(owner) => {
              if (!isEditing) setIsEditing(true);
              setEditedData({ ...editedData, owner });
            }}
            onPermissionsChange={(permissions) => {
              if (!isEditing) setIsEditing(true);
              setEditedData({ ...editedData, edit_permissions: permissions });
            }}
            disabled={!permissions.canEdit}
            selectedGameId={selectedGame?.id}
          />
        </div>
      </div>

      {/* Create New TMS Keys button */}
      {permissions.canEdit && (
        <div className="p-3 border-t border-border bg-background">
          <Button
            onClick={handleRegenerateTranslationKeys}
            disabled={isRegeneratingKeys}
            className="w-full"
            variant="outline"
          >
            {isRegeneratingKeys ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating New Keys...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Create New TMS Keys
              </>
            )}
          </Button>
          <p className="text-xs text-muted-foreground mt-2">
            Creates new independent translation keys for all translatable fields in this object
          </p>
        </div>
      )}

      {/* Save/Cancel buttons */}
      {permissions.canEdit && isEditing && (
        <div className="p-3 border-t border-border bg-muted flex gap-2">
          <Button
            onClick={onSave}
            disabled={isSaving}
            className="flex-1"
            variant="default"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save
              </>
            )}
          </Button>
          <Button
            onClick={onCancel}
            disabled={isSaving}
            variant="outline"
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}
